import * as React from "react";
import { Link, useRouteMatch } from "react-router-dom";
import {
  AccessReportStatusEnum,
} from "@app/access-reports/dashboard";
import { Message, MessageType } from "@app/notifications";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { ColumnConfig, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { toast, ToastOptions } from "react-toastify";
import { FlyoutLink } from "@app/shared/Flyouts";
import { FC, JSX, useEffect, useState } from "react";
import { NotificationContext } from "@app/notifications/NotificationContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import { formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserInfo, UserContext } from "@app/user/UserContext";
import { preventDefaultMouseEvent } from "@app/shared/utility/preventDefaultMouseEvent";
import AccessReportsService from "@app/access-reports/accessReportsService";
import { AccessReportRunStatusChangeNotificationMessagePayload } from "@app/access-reports/dashboard/shared/accessReportRunStatusChangeNotificationMessagePayload";
import { AccessReportsDashboardResponseModel } from "@app/access-reports/dashboard/shared/accessReportsDashboardResponseModel";
import { AccessReportUsageReportChangeNotificationMessagePayload } from "@app/access-reports/dashboard/shared/accessReportUsageReportChangeNotificationMessagePayload";
import { UsageReport } from "@app/access-reports/dashboard/UsageReport";
import { AccessReport } from "@app/access-reports/shared/accessReport";
import { handleFileLinkClick } from "@app/access-reports/shared/utility";

class AccessReportsPanelWaffleToppings {
  public key: string;
  public reportTitle: React.ReactNode;
  public internalClientId: string;
  public product: string;
  public companyName: string;
  public dateCreated: string;
  public createdBy: string;
  public status: string;
  public download: React.ReactNode;
  public clone: React.ReactNode;

  constructor(
    key: string,
    reportTitle: React.ReactNode,
    internalClientId: string,
    product: string,
    companyName: string,
    dateCreated: string,
    createdBy: string,
    status: string,
    download: React.ReactNode,
    clone: React.ReactNode
  ) {
    this.key = key;
    this.reportTitle = reportTitle;
    this.internalClientId = internalClientId;
    this.product = product;
    this.companyName = companyName;
    this.dateCreated = dateCreated;
    this.createdBy = createdBy;
    this.status = status;
    this.download = download;
    this.clone = clone;
  }
}

export const AccessReportsDashboard: FC = () => {
  const accessReportsService = React.useRef(new AccessReportsService()).current;
  const batchCount: number = React.useRef(100).current;
  const notificationContext = React.useContext(NotificationContext);
  const routeMatch = useRouteMatch();

  const [accessReports, setAccessReports] = useState<AccessReport[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [reportSearch, setReportSearch] = useState("");
  const [sidePanelVisible, setSidePanelVisible] = useState(false);
  const [sortDirection] = useState(SortDirection.Unknown);
  const [sortedColumn] = useState<keyof AccessReportsPanelWaffleToppings>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    fetchAccessReports();
    notificationContext.subscribe(handleNotifications);

    return () => {
      notificationContext.unsubscribe(handleNotifications);
    }
  }, []);

  const handleUsageReportToast = (status: AccessReportStatusEnum): void => {
    toast.dismiss();

    const toastOptions: ToastOptions = {
      closeOnClick: true,
      containerId: "z-index-9999",
    };

    switch (status) {
      case AccessReportStatusEnum.Canceled:
        toast.error("Your usage report was canceled", toastOptions);
        break;
      case AccessReportStatusEnum.Created:
        toast.info("Your usage report has been created", toastOptions);
        break;
      case AccessReportStatusEnum.Failed:
        toast.error("Your usage report has failed", toastOptions);
        break;
      case AccessReportStatusEnum.Running:
        toast.info("Your usage report is being processed", toastOptions);
        break;
      case AccessReportStatusEnum.Succeeded:
        toast.success("Your usage report was successfully created", toastOptions);
        break;
      default:
        break;
    }
  }

  const hideSidePanel = (): void => {
    if (sidePanelVisible) {
      setSidePanelVisible(false);
    }
  }

  const fetchAccessReports = async (
    isSorting = false,
    isFiltering = false,
    newOffset: number = offset,
    newSortedColumn: keyof AccessReportsPanelWaffleToppings = sortedColumn,
    newSortDirection: SortDirection = sortDirection,
    newReportSearch: string = reportSearch,
    newIncludeArchived = includeArchived,
  ): Promise<void> => {
    let newAccessReports: AccessReport[] = accessReports;
    let newBatchCount: number = batchCount;
    let newTotalCount: number = totalCount;

    if (isSorting && newSortedColumn && newSortDirection) {
      // if sorting, return the same amount of records that are currently displayed,
      // without the need for any offsets
      newBatchCount = accessReports.length;
      newOffset = 0;
    }

    if (isFiltering) {
      newOffset = 0;
    }

    if (isSorting && (newSortDirection === SortDirection.None || newSortDirection === SortDirection.Unknown)) {
      // this occurs when we're undoing a sort
      newSortedColumn = null;
      newSortDirection = SortDirection.Unknown;
    }

    const accessReportsDashboardModel: AccessReportsDashboardResponseModel = await accessReportsService.getAccessReports(
      newBatchCount,
      newOffset,
      newReportSearch,
      newIncludeArchived,
      newSortDirection,
      newSortedColumn,
    );

    if (accessReportsDashboardModel) {
      if (isSorting || isFiltering) {
        // sorting or filtering, meaning we should just replace the old records with the new ones
        newAccessReports = accessReportsDashboardModel.accessReports;
      } else {
        // acquiring more records (either on load, or on load more click),
        // so the new batch needs to be appended
        newAccessReports = newAccessReports.concat(accessReportsDashboardModel.accessReports);
      }

      newOffset = newOffset + newBatchCount;

      newTotalCount = accessReportsDashboardModel.totalCount;
    }

    let newIsFiltered: boolean = isFiltered;
    if (isFiltering && newReportSearch) {
      // for the purpose of displaying the "load more" button
      newIsFiltered = true;
    }

    setAccessReports(newAccessReports);
    setIncludeArchived(newIncludeArchived);
    setIsFiltered(newIsFiltered);
    setLoading(false);
    setOffset(newOffset);
    setTotalCount(newTotalCount);
  }

  const getColumnConfig = (user: UserInfo): ColumnConfig => {
    const columns: ColumnConfig = {
      clone: {
        displayText: "Clone",
        noSort: true,
        testId: "clone",
      },
      companyName: {
        displayText: "Company Name",
        testId: "companyName",
      },
      createdBy: {
        displayText: "Created By",
        hidden: !user.roles.includes(Roles.ACCESS_REPORTS_ADMIN),
        testId: "createdBy",
      },
      dateCreated: {
        displayText: "Date Created",
        testId: "dateCreated",
      },
      download: {
        displayText: "Download",
        noSort: true,
        testId: "download",
      },
      internalClientId: {
        displayText: "Internal Client Identifier",
        testId: "internalClientId",
      },
      product: {
        displayText: "Product",
        testId: "product",
      },
      reportTitle: {
        displayText: "Report Title",
        testId: "reportTitle",
      },
      status: {
        displayText: "Status",
        testId: "status",
      },
    };

    return columns;
  }

  const buildWaffleToppings = (accessReports: AccessReport[]): AccessReportsPanelWaffleToppings[] => {
    const waffleToppings: AccessReportsPanelWaffleToppings[] = accessReports.map((ar, index) => {
      const key: string = ar.accessReportId.toString();

      const reportTitle: React.ReactNode = (
        <Link
          className="demibold"
          id={`access-reports-dashboard__dashboard--report-title-link-${index}`}
          to={{
            pathname: `${routeMatch.url}/overview/${ar.accessReportId}`,
          }}
        >
          {!!ar.reportTitle ? ar.reportTitle : `Access Report ${ar.accessReportId}`}
        </Link>
      );

      const internalClientId: string = ar.internalClientId;

      const product: string = ar.productName;

      const companyName: string = ar.companyName;

      const dateCreated: string = formatUnconvertedDateCustom(ar.dateCreated, "YYYY-MM-DD hh:mmA");

      const createdBy: string = ar.createdBy;

      const status: string = ar.status;

      const download: React.ReactNode = ar.status === AccessReportStatusEnum.Succeeded && (
        <React.Fragment>
          <TooltipWrapper text="Download" triggerWidth="100%">
            <QESButton
              qesTestId={`access-reports-dashboard__dashboard--pdf-download-${index}`}
              qesType="secondary"
              onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(ar, true, "PDF"); }}
              className="demibold p-0"
            >
              PDF
            </QESButton>
          </TooltipWrapper>

          <TooltipWrapper containerClassName="ms-3" text="Download" triggerWidth="100%">
            <QESButton
              qesTestId={`access-reports-dashboard__dashboard--excel-download-${index}`}
              qesType="secondary"
              onClick={(e) => { preventDefaultMouseEvent(e); handleFileLinkClick(ar, true, "XLSX"); }}
              className="demibold p-0"
            >
              Excel
            </QESButton>
          </TooltipWrapper>
        </React.Fragment>
      );

      const clone: React.ReactNode = (
        <Link
          id={`access-reports-dashboard__dashboard--clone-${index}`}
          className="demibold"
          to={{
            pathname: `${routeMatch.path}/wizard/1`,
            state: {
              accessReportId: ar.accessReportId,
            },
          }}
        >
          Clone
        </Link>
      );

      return new AccessReportsPanelWaffleToppings(key, reportTitle, internalClientId, product, companyName, dateCreated, createdBy, status, download, clone);
    });

    return waffleToppings;
  }

  const customSort = (records: AccessReportsPanelWaffleToppings[], column: keyof AccessReportsPanelWaffleToppings, direction: string): AccessReportsPanelWaffleToppings[] => {
    let sortDirection: SortDirection = null;
    switch (direction) {
      case SortDirections.Asc:
        sortDirection = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        sortDirection = SortDirection.Descending;
        break;
      case SortDirections.None:
        sortDirection = SortDirection.None;
        break;

      default:
        sortDirection = SortDirection.Unknown;
    }

    fetchAccessReports(true, false, offset, column, sortDirection, reportSearch, includeArchived);

    return records;
  }

  const handleNotifications = async (message: Message): Promise<void> => {
    switch (message?.notificationType) {
      case MessageType.AccessReportsRunStatusChange:
        const projectRunStatusChangePayload: AccessReportRunStatusChangeNotificationMessagePayload = message.payload as AccessReportRunStatusChangeNotificationMessagePayload;

        if (!!projectRunStatusChangePayload) {
          const newAccessReports: AccessReport[] = accessReports;
          const accessReport: AccessReport = newAccessReports.find(x => x.accessReportId === projectRunStatusChangePayload.accessReportId);

          if (!!accessReport) {
            accessReport.status = projectRunStatusChangePayload.status;

            setAccessReports(newAccessReports);
          }
        }

        break;
      case MessageType.AccessReportsUsageReportStatusChange:
        const usageReportStatusChangePayload: AccessReportUsageReportChangeNotificationMessagePayload = message.payload as AccessReportUsageReportChangeNotificationMessagePayload;

        if (!!usageReportStatusChangePayload) {
          handleUsageReportToast(usageReportStatusChangePayload.status);
        }

        break;
      default:
        break;
    }
  }

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setReportSearch(event.target.value);
  }

  const onClearAllClick = (): void => {
    setIsFiltered(false);
    setReportSearch("");

    fetchAccessReports(false, true, offset, null, SortDirection.Unknown, "");
  }

  const renderUserReportSidePanel = (clientId: number, username: string): JSX.Element => {
    return (
      <UsageReport
        clientId={clientId}
        handleUsageReportToast={handleUsageReportToast}
        username={username}
      />
    );
  }

  const setArchiveMode = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!loading) {
      setIncludeArchived((prevIncludeArchived) => !prevIncludeArchived);

      await fetchAccessReports(true, isFiltered, 0, sortedColumn, sortDirection, reportSearch, !includeArchived);
    }
  }

  const pageTitle = "Network Access Report Dashboard";
  return (
    <UserContext.Consumer>
      {(user: UserInfo) => (
        <Page className="bg-white" documentTitle="Access Reports">
          <PageInner>
            <PageSection>
              <div className="row">
                <div className="col-12">
                  <PageHeader title={pageTitle}>
                    <PageHeaderLeft>
                      <RoleRestricted allowedRoles={[Roles.ACCESS_REPORTS_WIZARD_ADMIN]}>

                        <span className="ml5">
                          <QesIcon iconName={QesIcons.Archive} description="Archive" className="mb1" /> <a href="#" className={(loading) ? "home-link-disabled" : ""} id="access-reports-dashboard__dashboard--archive" onClick={setArchiveMode}>{includeArchived ? "Unarchived" : "Archived"}</a>
                        </span>

                      </RoleRestricted>
                    </PageHeaderLeft>
                    <PageHeaderRight>
                      <Link
                        className="qes-button qes-button-primary float-end"
                        id="access-reports-dashboard__dashboard--request-new-report"
                        type="button"
                        to={{
                          pathname: `${routeMatch.path}/wizard/1`,
                        }}
                      >
                        Request a New Report
                      </Link>
                    </PageHeaderRight>
                  </PageHeader>
                </div>
              </div>

              {loading ?
                <div className="row">
                  <div className="col-12">
                    <div className="text-center">
                      <LoadingSpinner />
                    </div>
                  </div>
                </div> :
                totalCount > 0 ?
                  <React.Fragment>
                    {totalCount > 1 &&
                      <div className="row">
                        <div className="col-12">
                          <div className="card card-static">
                            <div className="card-body">
                              <div className="row">
                                <div className="col-10">
                                  <QESTextInput
                                    containerClassName="access-reports-dashboard-filter-input"
                                    testId="access-reports-dashboard__dashboard--search-input"
                                    label="Search Previous Reports"
                                    onChange={(event) => onSearchChange(event)}
                                    placeholder="Search Internal Client Identifiers, dates, titles..."
                                    value={reportSearch}
                                  />
                                </div>

                                <div className="col-2 position-relative">
                                  <div id="access-reports-dashboard-filter-buttons">
                                    <QESButton
                                      qesTestId="access-reports-dashboard__dashboard--search"
                                      qesType="secondary"
                                      onClick={() => fetchAccessReports(false, true)}
                                      className="demibold"
                                    >
                                      Search
                                    </QESButton>

                                    <QESButton
                                      qesTestId="access-reports-dashboard__dashboard--clear"
                                      qesType="secondary"
                                      onClick={() => onClearAllClick()}
                                      className="demibold"
                                    >
                                      Clear
                                    </QESButton>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    {accessReports.length > 0 ? (
                      <>
                        <div className="row">
                          <div className="col-12">
                            <Waffles<AccessReportsPanelWaffleToppings>
                              bulkActionBar={user.roles.includes(Roles.ACCESS_REPORTS_ADMIN) && (
                                <span className="bulk-action-group">
                                  <FlyoutLink
                                    classNames="qes-button-secondary align-self-center p-0"
                                    content={{
                                      body: renderUserReportSidePanel(user?.clientId, user?.displayName),
                                      title: "Usage Report",
                                    }}
                                    panelId="access-reports-dashboard__dashboard--create-usage-report"
                                    isVisible={sidePanelVisible}
                                    size="20"
                                    testId="access-reports-dashboard__dashboard--create-usage-report-link"
                                    onClose={hideSidePanel}
                                  >
                                    <QesIcon className="me-1 mb-1" iconName={QesIcons.DownloadCloudV1} description="Download" />
                                    Create Usage Report
                                  </FlyoutLink>
                                </span>
                              )}
                              columns={getColumnConfig(user)}
                              customSort={customSort}
                              data={buildWaffleToppings(accessReports) || []}
                              fullWidth={true}
                              passThroughSorting={true}
                              testId="access-reports-dashboard__dashboard--all-reports"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="row">
                        <div className="col-12">
                          <div className="text-center text-muted">
                            There are no files available.
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment> :
                  <div className="row">
                    <div className="col-12">
                      <div className="text-center text-muted">
                        There are no files available.
                      </div>
                    </div>
                  </div>
              }

              {(accessReports.length < totalCount && !isFiltered) && (
                <div className="row">
                  <div className="col-12">
                    <div className="text-center">
                      <QESButton
                        qesTestId="access-reports-dashboard__dashboard--load-more"
                        className="mt-3"
                        onClick={() => fetchAccessReports()}
                      >
                        Load More
                      </QESButton>
                    </div>
                  </div>
                </div>
              )}
            </PageSection>
          </PageInner>
        </Page>
      )}
    </UserContext.Consumer>
  );
};
